import { Inject, Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as ObjectActions from './firmware-manager.actions';
import * as AppActions from '../app/actions';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { GeneralApiResponseInterface } from '../../layout/main/main.model';
import { FirmwareInterface } from './firmware-manager.model';
import { IotDeviceManagementPortalAppState } from '../iotDeviceManagementPortalApp.reducer';
import { Store } from '@ngrx/store';
import { PageFiltersAndSettingsService } from '../../shared/service/page-filters-and-settings/page-filters-and-settings.service';

@Injectable()
export class FirmwareManagerEffects {
  constructor(
    private actions$: Actions<ObjectActions.FirmwareManagerActions>,
    private http: HttpClient,
    private readonly store: Store<IotDeviceManagementPortalAppState>,
    private readonly pageFiltersAndSettings: PageFiltersAndSettingsService,
    @Inject('API_BASE_URL') private readonly baseUrl: string,
  ) {}

  private FIRMWARE_URLS = {
    GET: {
      FIRMWARE: '/firmware',
    },
    POST: {
      FIRMWARE: '/firmware',
      FIRMWARE_UPLOAD: '/file-upload',
    },
    PATCH: {
      FIRMWARE: '/firmware',
    },
    DELETE: {
      FIRMWARE: '/firmware',
      FIRMWARE_BULK: '/firmware/bulk/delete',
    },
  };

  @Effect()
  getFirmwares$ = this.actions$.pipe(
    ofType(ObjectActions.FirmwareManagementActionTypes.GetFirmwares),
    switchMap((objectData: ObjectActions.GetFirmwares) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.FIRMWARE_URLS.GET.FIRMWARE}`;
      let params: HttpParams = new HttpParams();

      params = params
        .append('page', String(objectData.query.page))
        .append('limit', String(objectData.query.pageSize || 1000));

      if (objectData.query.order) {
        params = params
          .set('order', `${objectData.query.order}`)
          .set('orderType', `${objectData.query.orderType.toUpperCase()}`);
      }

      if (objectData.query.search !== undefined) {
        params = params.set('searchType', String('firmware_search'));
        params = params.set('search', String(objectData.query.search));
      }

      if (objectData.query.startDate) {
        params = params.set('startDate', `${objectData.query.startDate}`).set('endDate', `${objectData.query.endDate}`);
      }

      return this.http
        .get<GeneralApiResponseInterface<FirmwareInterface[]>>(url, {
          params,
        })
        .pipe(
          switchMap((response: GeneralApiResponseInterface<FirmwareInterface[]>) => {
            return of(new ObjectActions.GetFirmwaresCompleted(response['data'], response['total']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );


  @Effect()
  createFirmware$ = this.actions$.pipe(
    ofType(ObjectActions.FirmwareManagementActionTypes.CreateFirmware),
    switchMap((objectData: ObjectActions.CreateFirmware) => {
      return this.http
        .post<GeneralApiResponseInterface<FirmwareInterface[]>>(`${this.baseUrl}${this.FIRMWARE_URLS.POST.FIRMWARE}`, {
          ...objectData.payload,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.CreateFirmwareCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getFirmware$ = this.actions$.pipe(
    ofType(ObjectActions.FirmwareManagementActionTypes.GetFirmware),
    switchMap((device: ObjectActions.GetFirmware) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.FIRMWARE_URLS.GET.FIRMWARE}/${device.deviceId}`;
      return this.http.get<GeneralApiResponseInterface<FirmwareInterface>>(url).pipe(
        switchMap((response) => {
          return of(new ObjectActions.GetFirmwareCompleted(response), new AppActions.HideLoader());
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );

  @Effect()
  updateDevice$ = this.actions$.pipe(
    ofType(ObjectActions.FirmwareManagementActionTypes.UpdateFirmware),
    switchMap((objectData: ObjectActions.UpdateFirmware) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.FIRMWARE_URLS.PATCH.FIRMWARE}/${objectData.id}`;
      return this.http
        .patch<GeneralApiResponseInterface<FirmwareInterface>>(url, {
          ...objectData.payload,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.UpdateFirmwareCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  deleteFirmware = this.actions$.pipe(
    ofType(ObjectActions.FirmwareManagementActionTypes.DeleteFirmware),
    switchMap((objectData: ObjectActions.DeleteFirmware) => {
      this.store.dispatch(new AppActions.ShowLoader());
      let url = `${this.baseUrl}${this.FIRMWARE_URLS.DELETE.FIRMWARE}`;

      if (Array.isArray(objectData.payload) && objectData.payload.length > 1) {
        const httpOptions = {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          body: {
            firmwares: objectData.payload,
          },
        };
        url = `${this.baseUrl}${this.FIRMWARE_URLS.DELETE.FIRMWARE_BULK}`;
        return this.http.delete<GeneralApiResponseInterface<FirmwareInterface[]>>(url, httpOptions).pipe(
          switchMap((response) => {
            return of(new ObjectActions.DeleteFirmwareCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }
      return this.http.delete<GeneralApiResponseInterface<FirmwareInterface[]>>(`${url}/${objectData.payload[0]}`).pipe(
        switchMap((response) => {
          return of(new ObjectActions.DeleteFirmwareCompleted(response['data']), new AppActions.HideLoader());
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );


  @Effect()
  uploadFirmware$ = this.actions$.pipe(
    ofType(ObjectActions.FirmwareManagementActionTypes.UploadFirmware),
    switchMap((objectData: ObjectActions.UploadFirmware) => {
      let file: File = objectData.file;
      let firmwareId: number = objectData.firmwareId;
      let formData:FormData = new FormData();
      if (objectData.file !== null) {
        formData.append('firmwarefile', file, file.name);
      }
      // const httpOptions = {
      //   headers: new HttpHeaders({ 'Content-Type': 'multipart/form-data;boundary=----WebKitFormBoundaryyrV7KO0BoCBuDbTL', 'Accept': 'application/json' }),
      // };
      return this.http.post(`${this.baseUrl}${this.FIRMWARE_URLS.POST.FIRMWARE}${this.FIRMWARE_URLS.POST.FIRMWARE_UPLOAD}/${firmwareId}`, formData, {})
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.UploadFirmwareCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );
}
